// src/pages/admin/CourseLectures/lectures/Lecture03Stellenausschreibung.tsx
import React, { useState, useRef } from "react";
import {
  Checklist,
  InfoBox,
  AgendaCard,
  TaskList,
} from "../components/LectureUI";

// ── Shared Accordion ──────────────────────────────────────────────
const Accordion: React.FC<{
  id: string;
  num: string;
  icon: string;
  title: string;
  openId: string;
  onToggle: (id: string) => void;
  children: React.ReactNode;
}> = ({ id, num, icon, title, openId, onToggle, children }) => {
  const isOpen = openId === id;
  const ref = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    onToggle(id);
    if (!isOpen) {
      setTimeout(() => {
        ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    }
  };

  return (
    <div
      ref={ref}
      className={`mb-3 rounded-xl border overflow-hidden shadow-sm ${isOpen ? "border-[#009fe3]/50" : "border-gray-200"} bg-white`}
    >
      <button
        onClick={handleToggle}
        className={`w-full flex items-center gap-3 px-4 py-3.5 text-left transition-colors ${isOpen ? "bg-blue-50" : "hover:bg-gray-50"}`}
      >
        <div
          className={`flex-shrink-0 flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wide transition-colors ${isOpen ? "bg-[#009fe3] text-white" : "bg-gray-100 text-gray-500"}`}
        >
          <span>{icon}</span>
          <span className="hidden sm:inline">{num}</span>
        </div>
        <span
          className={`flex-1 text-sm font-bold ${isOpen ? "text-[#0369a1]" : "text-gray-800"}`}
        >
          {title}
        </span>
        <span
          className={`flex-shrink-0 text-[#009fe3] transition-transform duration-200 text-sm ${isOpen ? "rotate-180" : ""}`}
        >
          ▾
        </span>
      </button>
      {isOpen && (
        <div className="px-4 pb-4 pt-2 border-t border-gray-100">
          {children}
        </div>
      )}
    </div>
  );
};

// ── PAR Donut Diagram (recreated from PDF slide) ──────────────────
const PARDiagram: React.FC = () => (
  <div className="flex flex-col sm:flex-row items-center gap-6 py-4">
    {/* SVG Donut — 3 equal 120° segments, mathematically correct */}
    <div className="flex-shrink-0">
      <svg viewBox="0 0 220 220" width="200" height="200" className="mx-auto">
        {/* 03 — top (dark teal): 210° → 330° */}
        <path
          d="M 40.72 70.00 A 80 80 0 0 1 179.28 70.00 L 149.84 87.00 A 46 46 0 0 0 70.16 87.00 Z"
          fill="#00796b"
        />
        {/* 01 — bottom-left (light teal): 90° → 210° */}
        <path
          d="M 110.00 190.00 A 80 80 0 0 1 40.72 70.00 L 70.16 87.00 A 46 46 0 0 0 110.00 156.00 Z"
          fill="#4db6ac"
        />
        {/* 02 — bottom-right (medium teal): 330° → 90° */}
        <path
          d="M 179.28 70.00 A 80 80 0 0 1 110.00 190.00 L 110.00 156.00 A 46 46 0 0 0 149.84 87.00 Z"
          fill="#26a69a"
        />
        {/* White donut hole */}
        <circle cx="110" cy="110" r="46" fill="white" />
        {/* Segment number labels at ring midpoints */}
        <text
          x="110"
          y="47"
          fill="white"
          fontSize="13"
          fontWeight="bold"
          textAnchor="middle"
          dominantBaseline="middle"
        >
          03
        </text>
        <text
          x="55.44"
          y="141.50"
          fill="white"
          fontSize="13"
          fontWeight="bold"
          textAnchor="middle"
          dominantBaseline="middle"
        >
          01
        </text>
        <text
          x="164.56"
          y="141.50"
          fill="white"
          fontSize="13"
          fontWeight="bold"
          textAnchor="middle"
          dominantBaseline="middle"
        >
          02
        </text>
      </svg>
    </div>

    {/* Labels */}
    <div className="flex flex-col gap-4 w-full">
      {[
        {
          num: "01",
          color: "bg-[#4db6ac]",
          label: "Problem",
          sub: "Was war schwierig?",
        },
        {
          num: "02",
          color: "bg-[#26a69a]",
          label: "Action",
          sub: "Was hast du getan?",
        },
        {
          num: "03",
          color: "bg-[#00796b]",
          label: "Result",
          sub: "Wie ging es aus?",
        },
      ].map((item) => (
        <div key={item.num} className="flex items-center gap-3">
          <div
            className={`w-9 h-9 rounded-full ${item.color} flex items-center justify-center text-white text-xs font-bold flex-shrink-0`}
          >
            {item.num}
          </div>
          <div>
            <p className="text-sm font-bold text-gray-800">{item.label}</p>
            <p className="text-xs text-gray-500">{item.sub}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ── Example answer card ───────────────────────────────────────────
const ExampleCard: React.FC<{
  question: string;
  method: string;
  color: string;
  items: { label: string; text: string }[];
  conclusion: string;
  conclusionColor: string;
}> = ({ question, method, color, items, conclusion, conclusionColor }) => (
  <div className={`rounded-xl border ${color} p-4`}>
    <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">
      Beispielfrage:
    </p>
    <p className="text-sm font-bold text-gray-800 mb-3 italic">„{question}"</p>
    <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">
      Antwort mit {method}:
    </p>
    <div className="space-y-1.5 mb-3">
      {items.map((item, i) => (
        <div key={i} className="flex items-start gap-2 text-sm">
          <span className="font-bold text-gray-700 flex-shrink-0 w-5">
            {item.label}:
          </span>
          <span className="text-gray-600">{item.text}</span>
        </div>
      ))}
    </div>
    <p className={`text-xs font-bold ${conclusionColor}`}>{conclusion}</p>
  </div>
);

// ── Main Component ────────────────────────────────────────────────
const Lecture03Stellenausschreibung: React.FC = () => {
  const [openSection, setOpenSection] = useState<string>("");
  const toggle = (id: string) =>
    setOpenSection((prev) => (prev === id ? "" : id));

  return (
    <div>
      {/* Recap */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-4 mb-4">
        <p className="text-xs font-bold text-amber-700 uppercase tracking-wide mb-2">
          📋 Aufgaben aus Lektion 02 — erledigt?
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {[
            "Lebenslauf erstellen",
            "XYZ-Regel verstehen",
            "Stellenausschreibung auf ausbildung.de anschauen",
            "Fokus auf B2-Sprachniveau & Sprachzeugnis",
          ].map((t, i) => (
            <div
              key={i}
              className="flex items-center gap-1.5 text-xs text-amber-800"
            >
              <span className="w-4 h-4 rounded border border-amber-400 flex-shrink-0 bg-white" />
              {t}
            </div>
          ))}
        </div>
      </div>

      {/* Agenda */}
      <AgendaCard
        items={[
          { num: "1", label: "Methoden Kennenlernen" },
          { num: "2", label: "XYZ-Regel" },
          { num: "3", label: "STAR-Methode" },
          { num: "4", label: "PAR-Methode" },
          { num: "5", label: "Methoden Anwenden" },
          { num: "6", label: "Stellenausschreibung lesen" },
          { num: "7", label: "Keywords erkennen" },
        ]}
      />

      {/* 01 - Methoden Kennenlernen */}
      <Accordion
        id="01"
        num="ABSCHNITT 01"
        icon="🧠"
        title="Methoden Kennenlernen"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="mt-1 mb-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <p className="text-sm font-bold text-gray-700 mb-1">
            Welche Methode hilft mir bei der Ausbildungssuche?
          </p>
          <p className="text-sm text-gray-500 mb-2">
            Viele Ausbildungssuchende fragen sich:
          </p>
          <p className="text-sm text-gray-600 italic border-l-2 border-[#009fe3] pl-3 mb-3">
            „Was soll ich sagen, wenn ich noch keine Berufserfahrung habe?"
          </p>
          <p className="text-sm text-gray-500">
            Genau dafür gibt es diese 3 Methoden:
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            {
              name: "XYZ-Methode",
              icon: "✍️",
              desc: "Kurz & stark fürs Anschreiben",
              color: "bg-blue-50 border-blue-200 text-blue-800",
            },
            {
              name: "STAR-Methode",
              icon: "⭐",
              desc: "Perfekt fürs Vorstellungsgespräch",
              color: "bg-purple-50 border-purple-200 text-purple-800",
            },
            {
              name: "PAR-Methode",
              icon: "🔄",
              desc: "Problemfokussiert & lösungsorientiert",
              color: "bg-teal-50 border-teal-200 text-teal-800",
            },
          ].map((m, i) => (
            <div
              key={i}
              className={`rounded-xl border p-3 text-center ${m.color}`}
            >
              <div className="text-2xl mb-1">{m.icon}</div>
              <p className="text-sm font-bold">{m.name}</p>
              <p className="text-[11px] mt-1 opacity-80">{m.desc}</p>
            </div>
          ))}
        </div>
      </Accordion>

      {/* 02 - XYZ-Regel */}
      <Accordion
        id="02"
        num="ABSCHNITT 02"
        icon="✍️"
        title="XYZ-Regel — Kurz & stark fürs Anschreiben"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-3">
          Die XYZ-Regel hilft dir, kurze und starke Sätze zu schreiben.
        </p>

        {/* XYZ 3-col visual */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          {[
            {
              letter: "X",
              desc: "Was habe ich gemacht?",
              color: "bg-blue-500",
            },
            {
              letter: "Y",
              desc: "Wie habe ich es gemacht?",
              color: "bg-blue-600",
            },
            {
              letter: "Z",
              desc: "Was war das Ergebnis?",
              color: "bg-blue-700",
            },
          ].map((item) => (
            <div
              key={item.letter}
              className="bg-white rounded-xl border border-blue-200 p-2 sm:p-3 text-center"
            >
              <div
                className={`w-8 h-8 rounded-full ${item.color} text-white font-extrabold text-sm flex items-center justify-center mx-auto mb-2`}
              >
                {item.letter}
              </div>
              <p className="text-[10px] sm:text-xs font-semibold text-gray-700 leading-tight">
                {item.desc}
              </p>
            </div>
          ))}
        </div>

        {/* Im Anschreiben context */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
          <p className="text-xs font-bold text-gray-600 mb-2">
            Im Anschreiben — wenig Platz, maximale Wirkung:
          </p>
          {[
            "Wenig Platz — du musst dich kurz vorstellen",
            "Zeige, warum du geeignet bist",
            "Nutze Keywords direkt aus der Stellenanzeige",
          ].map((item, i) => (
            <div
              key={i}
              className="flex items-start gap-2 text-sm text-gray-600 mb-1"
            >
              <span className="text-[#009fe3] font-bold flex-shrink-0 mt-0.5">
                ›
              </span>{" "}
              {item}
            </div>
          ))}
        </div>

        {/* Example */}
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
          <p className="text-xs font-bold text-[#0369a1] uppercase tracking-wide mb-2">
            📝 Beispiel — Keyword:{" "}
            <span className="text-red-600">Zuverlässigkeit</span>
          </p>
          <div className="space-y-2 mb-3">
            {[
              {
                label: "X",
                text: "Ich habe meine Schulaufgaben immer pünktlich erledigt",
              },
              { label: "Y", text: "...indem ich meine Zeit gut geplant habe" },
              {
                label: "Z",
                text: "...sodass ich meine Leistungen verbessern konnte",
              },
            ].map((item) => (
              <div key={item.label} className="flex items-start gap-2 text-sm">
                <span className="w-5 h-5 rounded-full bg-[#009fe3] text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                  {item.label}
                </span>
                <span className="text-[#0369a1]">{item.text}</span>
              </div>
            ))}
          </div>
          <div className="bg-white rounded-lg border border-blue-200 p-3">
            <p className="text-xs font-bold text-gray-500 mb-1">
              Vollständiger Satz:
            </p>
            <p className="text-sm text-gray-700 italic">
              „Ich habe meine Schulaufgaben immer pünktlich erledigt (X), indem
              ich meine Zeit gut geplant habe (Y), sodass ich meine Leistungen
              verbessern konnte (Z)."
            </p>
          </div>
        </div>
      </Accordion>

      {/* 03 - STAR-Methode */}
      <Accordion
        id="03"
        num="ABSCHNITT 03"
        icon="⭐"
        title="STAR-Methode — Die beste Wahl fürs Vorstellungsgespräch"
        openId={openSection}
        onToggle={toggle}
      >
        {/* STAR 4 cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          {[
            {
              letter: "S",
              full: "Situation",
              question: "Wo warst du?",
              hint: "Schule, Projekt, Praktikum",
              color: "bg-purple-50 border-purple-200 text-purple-800",
            },
            {
              letter: "T",
              full: "Task (Aufgabe)",
              question: "Was war deine Aufgabe?",
              hint: "",
              color: "bg-violet-50 border-violet-200 text-violet-800",
            },
            {
              letter: "A",
              full: "Action (Handlung)",
              question: "Was hast du persönlich gemacht?",
              hint: "",
              color: "bg-indigo-50 border-indigo-200 text-indigo-800",
            },
            {
              letter: "R",
              full: "Result (Ergebnis)",
              question: "Was ist dabei rausgekommen?",
              hint: "",
              color: "bg-blue-50 border-blue-200 text-blue-800",
            },
          ].map((item) => (
            <div
              key={item.letter}
              className={`rounded-xl border p-3 ${item.color}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <div className="w-7 h-7 rounded-full bg-purple-600 text-white font-extrabold text-sm flex items-center justify-center flex-shrink-0">
                  {item.letter}
                </div>
                <span className="text-sm font-bold">{item.full}</span>
              </div>
              <p className="text-xs pl-9 opacity-80">{item.question}</p>
              {item.hint && (
                <p className="text-xs pl-9 opacity-60 italic">{item.hint}</p>
              )}
            </div>
          ))}
        </div>

        {/* Common questions */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
          <p className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-2">
            Im Vorstellungsgespräch bekommst du oft Fragen wie:
          </p>
          {[
            '"Erzählen Sie etwas über sich"',
            '"Wie arbeiten Sie im Team?"',
            '"Wie gehen Sie mit Aufgaben um?"',
          ].map((q, i) => (
            <div
              key={i}
              className="flex items-start gap-2 text-sm text-gray-700 mb-1"
            >
              <span className="text-purple-500 flex-shrink-0">❓</span> {q}
            </div>
          ))}
        </div>

        {/* Example answer */}
        <ExampleCard
          question="Wie arbeiten Sie im Team?"
          method="STAR"
          color="border-purple-200 bg-purple-50"
          items={[
            { label: "S", text: "In der Schule hatten wir ein Gruppenprojekt" },
            { label: "T", text: "Wir mussten eine Präsentation vorbereiten" },
            {
              label: "A",
              text: "Ich habe die Aufgaben verteilt und Inhalte recherchiert",
            },
            { label: "R", text: "Wir haben eine gute Note bekommen" },
          ]}
          conclusion="Der Betrieb sieht: Teamfähigkeit, Verantwortung, Einsatz → STAR = perfekt fürs Vorstellungsgespräch!"
          conclusionColor="text-purple-700"
        />
      </Accordion>

      {/* 04 - PAR-Methode */}
      <Accordion
        id="04"
        num="ABSCHNITT 04"
        icon="🔄"
        title="PAR-Methode — Probleme lösen"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-3">
          PAR konzentriert sich auf Problemlösung — zeigt deine
          Selbstständigkeit und Durchhaltevermögen.
        </p>

        {/* PAR Donut Diagram — recreated from slide */}
        <div className="rounded-xl border border-teal-200 bg-teal-50/40 p-4 mb-4">
          <PARDiagram />
        </div>

        {/* Example answer */}
        <ExampleCard
          question="Wie reagieren Sie bei Stress?"
          method="PAR"
          color="border-teal-200 bg-teal-50"
          items={[
            { label: "P", text: "Ich hatte viele Prüfungen in kurzer Zeit" },
            {
              label: "A",
              text: "Ich habe einen Lernplan gemacht und früh angefangen",
            },
            { label: "R", text: "Ich habe alle Prüfungen bestanden" },
          ]}
          conclusion="Der Betrieb sieht: Selbstständigkeit, Organisation, Durchhaltevermögen → PAR = Perfekt zum Lösen von Problemen!"
          conclusionColor="text-teal-700"
        />
      </Accordion>

      {/* 05 - Methoden Anwenden */}
      <Accordion
        id="05"
        num="ABSCHNITT 05"
        icon="⚖️"
        title="Methoden Anwenden — Wann welche Methode?"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm font-semibold text-gray-700 mb-3">
          Wähle die Methode je nach Situation:
        </p>
        <div className="space-y-3">
          {[
            {
              method: "XYZ",
              icon: "✍️",
              use: "Für das Anschreiben (Lebenslauf)",
              desc: "Kurz, prägnant — ideal wenn wenig Platz ist",
              tag: "Kurz",
              color: "bg-blue-50 border-blue-200",
              tagColor: "bg-blue-100 text-blue-700",
            },
            {
              method: "STAR",
              icon: "⭐",
              use: "Für das Vorstellungsgespräch",
              desc: "Vollständig, interview-tauglich — erzählt die ganze Geschichte",
              tag: "Ausführlich",
              color: "bg-purple-50 border-purple-200",
              tagColor: "bg-purple-100 text-purple-700",
            },
            {
              method: "PAR",
              icon: "🔄",
              use: "Für Problem-Fragen im Interview",
              desc: "Problemfokus, lösungsorientiert, mittellang",
              tag: "Mittellang",
              color: "bg-teal-50 border-teal-200",
              tagColor: "bg-teal-100 text-teal-700",
            },
          ].map((item) => (
            <div
              key={item.method}
              className={`rounded-xl border p-3 ${item.color}`}
            >
              <div className="flex items-start gap-3">
                <span className="text-xl flex-shrink-0">{item.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <span className="text-sm font-bold text-gray-800">
                      {item.method}-Methode
                    </span>
                    <span
                      className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${item.tagColor}`}
                    >
                      {item.tag}
                    </span>
                  </div>
                  <p className="text-xs font-semibold text-gray-600">
                    {item.use}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">{item.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        <InfoBox variant="tip" icon="💡" title="Merksatz">
          XYZ = schreiben · STAR = erzählen · PAR = Problem lösen
        </InfoBox>
      </Accordion>

      {/* 06 - Stellenausschreibung lesen */}
      <Accordion
        id="06"
        num="ABSCHNITT 06"
        icon="📋"
        title="Stellenausschreibung richtig lesen"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="mb-3 p-3 bg-red-50 border border-red-100 rounded-lg">
          <p className="text-sm font-bold text-red-700">
            🔑 Wer LESEN kann, hat einen Vorteil — deswegen genau und richtig
            lesen!
          </p>
        </div>
        <div className="space-y-3">
          {[
            {
              title: "Unternehmensbeschreibung",
              icon: "🏢",
              desc: "Hier erfährst du, wer der Arbeitgeber ist, welche Werte oder Branche wichtig sind.",
              color: "border-blue-200 bg-blue-50 text-blue-800",
            },
            {
              title: "Deine Aufgaben / Tätigkeiten",
              icon: "📝",
              desc: "Das ist der Kern: Was sollst du später in der Ausbildung machen?",
              color: "border-green-200 bg-green-50 text-green-800",
            },
            {
              title: "Anforderungen / Qualifikationen",
              icon: "✅",
              desc: 'Hier steht, welche Fähigkeiten oder Eigenschaften erwartet werden. Beispiele: „Teamfähigkeit", „gute Deutschkenntnisse", „Interesse an Technik"',
              color: "border-orange-200 bg-orange-50 text-orange-800",
            },
            {
              title: "Bewerbungsmodalitäten",
              icon: "📅",
              desc: "Wie und bis wann du dich bewerben sollst.",
              color: "border-purple-200 bg-purple-50 text-purple-800",
            },
          ].map((item, i) => (
            <div key={i} className={`rounded-xl border p-3 ${item.color}`}>
              <div className="flex items-start gap-2.5">
                <span className="text-xl flex-shrink-0">{item.icon}</span>
                <div>
                  <p className="text-sm font-bold mb-0.5">{item.title}</p>
                  <p className="text-xs opacity-90">{item.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Accordion>

      {/* 07 - Keywords erkennen */}
      <Accordion
        id="07"
        num="ABSCHNITT 07"
        icon="🔍"
        title="Keywords erkennen"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-3">
          Beispiel: Ausbildung als{" "}
          <strong>Pflegefachmann / Pflegefachfrau</strong>. Keywords fallen in 4
          Kategorien:
        </p>
        <div className="space-y-3">
          {[
            {
              title: "Fachliche Fähigkeiten (Hard Skills)",
              icon: "🔧",
              color: "border-red-200 bg-red-50 text-red-800",
              items: [
                "Pflegedokumentation / EDV-Kenntnisse in Pflegeprogrammen",
                "Hygienemaßnahmen / Infektionsschutz",
              ],
            },
            {
              title: "Persönliche Eigenschaften (Soft Skills)",
              icon: "🤝",
              color: "border-blue-200 bg-blue-50 text-blue-800",
              items: ["Teamfähigkeit", "Kommunikationsstärke"],
            },
            {
              title: "Formale Voraussetzungen",
              icon: "📄",
              color: "border-green-200 bg-green-50 text-green-800",
              items: [
                "Schulabschluss: Mittlere Reife",
                "Mindestens 18 Jahre alt",
              ],
            },
            {
              title: "Aufgabenbezogene Begriffe",
              icon: "📋",
              color: "border-purple-200 bg-purple-50 text-purple-800",
              items: ["Vitalwerte kontrollieren", "Pflegepläne erstellen"],
            },
          ].map((cat, i) => (
            <div key={i} className={`rounded-xl border p-3 ${cat.color}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-base">{cat.icon}</span>
                <p className="text-sm font-bold">{cat.title}</p>
              </div>
              <div className="pl-6 space-y-1">
                {cat.items.map((item, j) => (
                  <div key={j} className="flex items-start gap-1.5 text-xs">
                    <span className="flex-shrink-0 mt-0.5">›</span>
                    <span className="opacity-90">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <InfoBox variant="tip" icon="💡" title="Strategie">
          Markiere alle Keywords in der Stellenanzeige und baue sie gezielt in
          deinen Lebenslauf und dein Anschreiben ein. So passierst du auch
          ATS-Systeme.
        </InfoBox>
      </Accordion>

      {/* Fazit */}
      <Accordion
        id="fazit"
        num="FAZIT"
        icon="🏁"
        title="Fazit"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
          <span className="text-2xl flex-shrink-0">✅</span>
          <div>
            <p className="text-sm font-bold text-green-800 mb-2">
              Wer Stellenanzeigen richtig liest und Methoden gezielt einsetzt,
              bewirbt sich besser.
            </p>
            <ul className="space-y-1">
              {[
                "XYZ für das Anschreiben — kurz und präzise",
                "STAR für das Vorstellungsgespräch — vollständige Geschichte",
                "PAR für Problemfragen — lösungsorientiert",
                "Keywords aus der Anzeige immer in Lebenslauf & Anschreiben einbauen",
              ].map((item, i) => (
                <li
                  key={i}
                  className="text-sm text-green-700 flex items-start gap-2"
                >
                  <span className="flex-shrink-0 mt-0.5">›</span> {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Accordion>

      {/* Aufgaben */}
      <Accordion
        id="aufgaben"
        num="AUFGABEN"
        icon="✅"
        title="Deine Aufgaben – Lektion 03"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="pt-1">
          <TaskList
            tasks={[
              "3 verschiedene Berufe wählen und passende Stellenanzeige auf ausbildung.de aussuchen",
              "Keywords markieren, Anzeige verstehen und mit eigenen Wörtern beschreiben können",
              "3 Lebensläufe nach den Keywords vorbereiten (je einen pro Beruf)",
              "Die gefragte Fähigkeit nach XYZ-Regel schreiben und nach STAR-Methode Antwort vorbereiten",
              "Fokus auf B2-Sprachniveau sowie auf das Sprachzeugnis",
            ]}
          />
        </div>
      </Accordion>
    </div>
  );
};

export default Lecture03Stellenausschreibung;
